import type { ElectronAPI } from '@/types/electron';
import { desktopBridge } from '@/services/desktopBridge';

/**
 * Opens a file using the appropriate method (Electron API, desktop bridge, or web message bridge)
 * @param filePath - Full path to the file to open
 * @param electronAPI - Optional Electron API instance (if in Electron environment)
 * @param isElectron - Whether running in Electron environment
 * @returns Promise resolving to success/error result
 */
export async function openFile(
  filePath: string,
  electronAPI?: ElectronAPI | null,
  isElectron?: boolean
): Promise<{ success: boolean; error?: string }> {
  // Try Electron API first if available
  if (isElectron && electronAPI?.openFile) {
    return await electronAPI.openFile(filePath);
  }

  // Try desktop bridge for web version
  const isDesktopAvailable = await desktopBridge.isDesktopAvailable();
  if (isDesktopAvailable) {
    return await desktopBridge.openFile(filePath);
  }

  // Fallback to web-message bridge in Electron
  if (isElectron && electronAPI?.handleWebMessage) {
    const bridgeResult = await electronAPI.handleWebMessage({
      type: 'open-file',
      payload: { filePath },
      id: Math.random().toString(36).substr(2, 9),
    });

    // Transform the result to match expected format
    if (bridgeResult.success && bridgeResult.data) {
      return bridgeResult.data as { success: boolean; error?: string };
    } else {
      return {
        success: false,
        error: bridgeResult.error || 'Failed to open file',
      };
    }
  }

  // No method available
  return {
    success: false,
    error:
      'Desktop app is not connected. Please ensure the desktop app is running.',
  };
}

export type RevealTarget = { path: string; kind: 'file' | 'folder' };

/**
 * Reveals a file or folder in the OS file manager
 * @param target - Path and kind (file selects in folder, folder opens directly)
 * @param electronAPI - Optional Electron API instance (if in Electron environment)
 * @param isElectron - Whether running in Electron environment
 * @returns Promise resolving to success/error result
 */
export async function revealInFileExplorer(
  target: RevealTarget,
  electronAPI?: ElectronAPI | null,
  isElectron?: boolean
): Promise<{ success: boolean; error?: string }> {
  const { path: filePath, kind } = target;

  if (kind === 'folder') {
    return openFile(filePath, electronAPI, isElectron);
  }

  if (isElectron && electronAPI?.showItemInFolder) {
    return await electronAPI.showItemInFolder(filePath);
  }

  if (isElectron && electronAPI?.handleWebMessage) {
    const bridgeResult = await electronAPI.handleWebMessage({
      type: 'show-item-in-folder',
      payload: { filePath },
      id: Math.random().toString(36).substr(2, 9),
    });

    if (bridgeResult.success && bridgeResult.data) {
      return bridgeResult.data as { success: boolean; error?: string };
    }

    return {
      success: false,
      error: bridgeResult.error || 'Failed to reveal file in folder',
    };
  }

  return {
    success: false,
    error:
      'Desktop app is not connected. Please ensure the desktop app is running.',
  };
}
